import React, { useEffect, useRef } from 'react';
import { useInput, TextInput, SelectInput, SimpleFormIterator, Toolbar, SaveButton } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import Link from '@material-ui/core/Link';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import CardActions from '@material-ui/core/CardActions';
import WarningIcon from '@material-ui/icons/WarningRounded';
import { ValidatingSaveButton } from './ValidatingSaveButton';

// Full-admin gate for platform-internal fields (e.g. platform margin).
// Deliberately excludes network_admin/network_manager: whitelabel org staff
// must not see the platform's take.
export function isAdminRole (permissions) {
  return permissions && (permissions.role === 'admin' || permissions.role === 'super_admin');
}

// Gate for the self-serve advertiser role: swaps in the restricted
// advertiser/* screens (no supply-side targeting, no platform margin) for
// campaigns, creatives and the account/spend-limit view.
export function isAdvertiserRole (permissions) {
  return !!permissions && permissions.role === 'advertiser';
}

// Save-only toolbar (no Delete button), shared by the advertiser self-serve
// Campaign and Creative forms (src/advertiser/campaigns.js,
// src/advertiser/creatives.js): an advertiser must not be able to delete
// their own campaigns/creatives, only save changes.
export const NoDeleteToolbar = (props) => (
  <Toolbar {...props}><SaveButton /></Toolbar>
);

// NoDeleteToolbar plus per-field validation feedback (NBU-9). Kept separate
// from NoDeleteToolbar rather than replacing it, because ValidatingSaveButton
// only pays for itself on a TabbedForm paired with ErrorAwareFormTabs - on a
// single-tab form react-admin's stock message is already adjacent to the field.
export const NoDeleteValidatingToolbar = (props) => (
  <Toolbar {...props}><ValidatingSaveButton /></Toolbar>
);

export function roundDouble (val, decimals) {
	const div = Math.pow(10, decimals || 2)
  return Math.round((val + Number.EPSILON) * div) / div;
}

export function formatLong(val) {
	return <span>{ formatLongData(val) }</span>;
}

export function long(val) {
	return formatLongData(val);
}

export function formatLongData(val) {
	val = val || 0;
	val = (isNaN(val)) ? 0 : val;
	return new Intl.NumberFormat().format(Number.parseFloat(val).toFixed(0));
}

export function double (val, currency, precision) {
	precision = precision || 2;
	return (currency ? currency : '$') + new Intl.NumberFormat().format(Number.parseFloat(val).toFixed(precision));
}

/**
 * A percentage, with the sign where a reader expects it.
 *
 * double(6.93, '%') renders "%6.93" - double() treats its second argument as a
 * currency PREFIX, so borrowing it for percentages puts the sign on the wrong
 * side. Returns '-' for a value that is not a finite number, so a missing
 * denominator reads as "no data" instead of "NaN%".
 */
export function percent (val, precision) {
	precision = (precision === undefined) ? 1 : precision;
	const num = Number.parseFloat(val);
	if (!Number.isFinite(num)) return '-';
	return new Intl.NumberFormat().format(num.toFixed(precision)) + '%';
}

export function formatDouble (val, currency, precision) {
	precision = precision || 2;
	return <span>{ double (val, currency, precision) }</span>;
	// return <span>{ (currency ? currency : '$') + Number.parseFloat(val).toFixed(precision) }</span>;
}

export function formatDateTime(val) {
	val = '' + val;
	return <span>{ val.substring(0, val.indexOf(':')).replace('T', ' ') + ':00' }</span>;
}

export function formatDate(val) {
	val = '' + val;
	return <span>{ val.substring(0, val.indexOf('T')) }</span>;
}

export function CustomDateField ({ record }) {
  let date = '' + record.date;
  date = date.substring(0, date.indexOf('T'))
  return <span><nobr>{date}</nobr></span>;
};

export const BIDDERS_REQUIRE_SITE_ID = [312, 313, 400, 410, 326, 330, 408, 323, 350, 320, 409, 363, 364
	, 365, 437, 332, 411, 425, 426, 383, 366, 384, 385, 386, 380, 442, 392, 1026];
export const BIDDERS_REQUIRE_SITE_ID_LABEL = {
	350: 'Zone ID',
	323: 'Zone ID',
	309: 'Zone ID',
	312: 'Placement ID',
	313: 'Placement ID',
	400: 'Placement ID',
	326: 'Placement ID',
	330: 'Placement ID',
	408: 'Placement ID',
	320: 'Placement ID',
	409: 'Placement ID',
	363: 'Placement ID',
	425: 'Placement ID',
	332: 'Placement ID',
	411: 'Placement ID',
	392: 'Placement ID',
	364: 'Zone ID',
  1026: 'Site ID',
	426: 'Zone ID',
	365: 'CRID',
	437: 'CRID',
	383: 'Tag ID',
	366: 'Placement ID',
	384: 'Placement ID',
	385: 'Placement ID',
	386: 'Placement Key',
};
export const BIDDERS_REQUIRE_SEAT_ID = [];
export const BIDDERS_REQUIRE_SEAT_ID_LABEL = {
};
export const BIDDERS_REQUIRE_PUBLISHER_ID = [364, 426, 1026];
export const BIDDERS_REQUIRE_PUBLISHER_ID_LABEL = {
	364: 'Site ID',
	426: 'Site ID',
  1026: 'Publisher ID'
};
export const BIDDERS_SELECTABLE = [
	// { id: 101, name: 'Google AdSense (101)' },
	{ id: 307, name: 'Google AdExchange Tag (307)' },
	{ id: 607, name: 'AdExchange Tag OpsCo (607)' },
	{ id: 606, name: 'NoBid Backfill Tag (606)' },
	// { id: 507, name: 'AdExchange Tag West Seven Media (507)' },
	// { id: 707, name: 'AdExchange Tag Media Tradecraft (707)' },
	// { id: 807, name: 'AdExchange Tag 152 Media (807)' },
	// { id: 907, name: 'AdExchange Tag SIA Setupad (907)' },
	// { id: 706, name: 'AdExchange Tag Geniee International (706)' },
	// { id: 705, name: 'AdExchange Tag Snigel (705)' },
	// { id: 711, name: 'AdExchange Tag 1XL (711)' },
	// { id: 713, name: 'AdExchange Tag VerticalScope (713)' },
	// { id: 715, name: 'AdExchange Tag Eight Network (715)' },
	// { id: 379, name: 'Bidswitch Banner DM (379)' },
	// { id: 309, name: 'Gumgum (309)' },
	{ id: 310, name: 'Sovrn (310)' },
	{ id: 312, name: 'Appnexus (312)' },
	{ id: 313, name: 'Appnexus Native (313)' },
	{ id: 400, name: 'Appnexus Video (400)' },
	{ id: 326, name: 'Appnexus 2 (326)' },
	{ id: 330, name: 'Appnexus 2 Native (330)' },
	{ id: 408, name: 'Appnexus 2 Video (408)' },
	{ id: 392, name: 'Inmobi Banner PBS (392)' },
	{ id: 350, name: 'Rubicon/Magnite (350)' },
	{ id: 323, name: 'Rubicon/Magnite DM (323)' },
	{ id: 354, name: 'Online Media Solutions Banner (354)' },
	{ id: 1002, name: 'Online Media Solutions Video (1002)' },
	// { id: 314, name: 'Yieldlift (314)' },
	// { id: 401, name: 'Gumgum Video (401)' },
	{ id: 320, name: 'Mediafuse Banner (320)' },
	{ id: 409, name: 'Mediafuse Video (409)' },
	{ id: 332, name: 'Sonobi (332)' },
	{ id: 411, name: 'Sonobi Video (411)' },
	{ id: '', name: '--- Mediafuse Network ---', disabled: true },
	{ id: 363, name: 'Appnexus Banner (MF Network) (363)' },
	{ id: 425, name: 'Appnexus Video (MF Network) (425)' },
	{ id: 380, name: 'Appnexus MF Banner (380)' },
	{ id: 442, name: 'Appnexus MF Video (442)' },
	{ id: 365, name: 'Media.net Banner (MF Network) (365)' },
	{ id: 437, name: 'Media.net Video (MF Network) (437)' },
	{ id: 385, name: 'OneTag Banner (MF Network) (385)' },
	{ id: 384, name: 'Rise Banner (MF Network) (384)' },
	{ id: 364, name: 'Rubicon/Magnite Banner (MF Network) (364)' },
	{ id: 426, name: 'Rubicon/Magnite Video (MF Network) (426)' },
	{ id: 386, name: 'Sharethrough Banner (MF Network) (386)' },
	{ id: 383, name: 'Sovrn Banner (MF Network) (383)' },
	{ id: 366, name: 'Unruly Banner (MF Network) (366)' },
  { id: '', name: '--- Mediatonik Network ---', disabled: true },
  { id: 1026, name: 'Rubicon/Magnite Banner (MediaTonik Network) (1026)' },
];
export const BIDDERS_PREDICTORS = [
	{ id: 0, name: '- No Default Bidder -' },
	// { id: 101, name: 'Google AdSense (101)' },
	{ id: 307, name: 'Google AdExchange Tag (307)' },
	{ id: 607, name: 'AdExchange Tag OpsCo (607)' },
	{ id: 606, name: 'NoBid Backfill Tag (606)' },
	// { id: 507, name: 'AdExchange Tag West Seven Media (507)' },
	// { id: 707, name: 'AdExchange Tag Media Tradecraft (707)' },
	// { id: 807, name: 'AdExchange Tag 152 Media (807)' },
	// { id: 907, name: 'AdExchange Tag SIA Setupad (907)' },
	// { id: 706, name: 'AdExchange Tag Geniee International (706)' },
	// { id: 705, name: 'AdExchange Tag Snigel (705)' },
	// { id: 711, name: 'AdExchange Tag 1XL (711)' },
	// { id: 713, name: 'AdExchange Tag VerticalScope (713)' },
	// { id: 715, name: 'AdExchange Tag Eight Network (715)' }
];
export const BIDDERS_PREDICTION_DISABLABLE = BIDDERS_PREDICTORS.filter(x=>x.id>101).map(x=>x.id);

/* ---------------------------------------------------------------------------
 * Budget pacing (NBI-20)
 * ------------------------------------------------------------------------- */

/**
 * How the budget coordinator releases a campaign's remaining budget each cycle.
 *
 * Labels are the ops form's originals, unchanged - shared so the ops and
 * advertiser campaign forms cannot describe the same setting two ways.
 */
export const BUDGET_PACING_CHOICES = [
	{ id: 'even', name: 'Even — spread delivery evenly through the day' },
	{ id: 'asap', name: 'ASAP — deliver as fast as possible' }
];

/**
 * Matches the server: `budget_pacing` absent or unknown is treated as `even`
 * (insights service/etl/budget_pacing.js resolvePacing). Applied as
 * `initialValue`, not `defaultValue`, so an existing campaign with no stored
 * value also displays "Even" - which is what it will actually do.
 */
export const DEFAULT_BUDGET_PACING = 'even';

export const BudgetPacingSelectInput = ({ source = 'budget_pacing', label = 'Budget Pacing', ...props }) => (
	<SelectInput
		source={source}
		label={label}
		choices={BUDGET_PACING_CHOICES}
		initialValue={DEFAULT_BUDGET_PACING}
		helperText={'Controls how quickly the budget is spent. Even keeps delivery steady all day, ' +
			'so the campaign is still running in the evening, and automatically makes up any ' +
			'shortfall later. ASAP delivers as fast as inventory allows, which can use up the ' +
			'budget early. Different from Pace %, which limits how often the campaign enters ' +
			'the auction.'}
		{...props}
	/>
);

/* ---------------------------------------------------------------------------
 * Timezone selection
 *
 * THREE vocabularies exist in this app and they are NOT interchangeable. Picking
 * the wrong one changes the string that gets stored or sent to a backend.
 *
 *  1. IANA_TIMEZONES below - canonical IANA ids ('America/New_York'). For
 *     values insights validates with Intl.DateTimeFormat: campaign flight
 *     bounds and the advertiser monthly-reset boundary. Use
 *     <TimezoneSelectInput>.
 *  2. REPORT_TIMEZONES below - POSIX-style aliases ('US/Eastern'). For report
 *     query parameters, which flow on to reporting backends and, for
 *     publishers.tam_config, to an external integration. Use
 *     <ReportTimezoneSelectInput>. Do NOT "modernise" these ids to canonical
 *     IANA names: the value is submitted, and no one has verified the consumers
 *     accept both spellings.
 *  3. The legacy `timezones` array in adcampaigns.js - contains NON-IANA ids
 *     such as 'Central European Time'. Backs Day Parting only. Those are real
 *     stored values on existing configs, so it must not be edited or merged
 *     into either list above.
 * ------------------------------------------------------------------------- */

/**
 * Canonical IANA zones, for anything insights validates with
 * Intl.DateTimeFormat.
 *
 * `isValidTimezone` (insights service/utils/flight_dates.js) rejects an unknown
 * name and SILENTLY FALLS BACK TO UTC, so a typo in a free-text field moves a
 * campaign's flight window - or an advertiser's billing month boundary - by
 * hours with no error anywhere in the stack. That silent fallback is why these
 * fields must be a select and never a text input.
 */
export const IANA_TIMEZONES = [
	{ id: 'America/New_York', name: '(GMT-05:00) America/New_York' },
	{ id: 'America/Chicago', name: '(GMT-06:00) America/Chicago' },
	{ id: 'America/Denver', name: '(GMT-07:00) America/Denver' },
	{ id: 'America/Los_Angeles', name: '(GMT-08:00) America/Los_Angeles' },
	{ id: 'UTC', name: '(GMT+00:00) UTC' },
	{ id: 'Europe/London', name: '(GMT+00:00) Europe/London' },
	{ id: 'Europe/Paris', name: '(GMT+01:00) Europe/Paris' },
	{ id: 'Europe/Berlin', name: '(GMT+01:00) Europe/Berlin' },
	{ id: 'Asia/Tokyo', name: '(GMT+09:00) Asia/Tokyo' },
	{ id: 'Australia/Sydney', name: '(GMT+11:00) Australia/Sydney' }
];

/**
 * US Eastern, the platform's business timezone: it is already the fallback the
 * budget coordinator uses for a campaign's budget DAY when flight_timezone is
 * absent or invalid (insights service/etl/budget_pacing.js), so defaulting
 * flight bounds to it keeps a new campaign's flight window and its daily budget
 * window on the same calendar day.
 *
 * A form default only - the insights server still defaults an ABSENT
 * flight_timezone to UTC (flight_dates.applyFlightFields), which is the
 * documented /api/v1 behaviour and is not changed by this.
 */
export const DEFAULT_TIMEZONE = 'America/New_York';

/** POSIX-style aliases used by the report screens. See the note above. */
export const REPORT_TIMEZONES = [
	'US/Eastern', 'UTC', 'US/Mountain', 'US/Pacific', 'US/Central',
	'Europe/London', 'Europe/Paris', 'Europe/Helsinki'
].map(id => ({ id, name: id }));

export const DEFAULT_REPORT_TIMEZONE = 'US/Eastern';

// Module-level so the object identity is stable across renders. react-final-form
// compares some input props by identity, and an inline literal is a fresh object
// every render - see the useStableArray note in common/filters/Filters.js for
// what that cost us once already.
const TIMEZONE_INPUT_STYLE = { width: '350px' };

/**
 * The one timezone picker for IANA-validated fields (campaign flight bounds,
 * advertiser monthly reset). Replaces per-screen copies that had drifted: the
 * ops campaign form had its own private choices list and the advertiser form
 * had a FREE TEXT input, which is how a typo could silently land as UTC.
 *
 * `source` is required; everything else has a sensible default and any prop can
 * be overridden (the spread is last), which is how the advertiser monthly-reset
 * field keeps its historical UTC default.
 */
export const TimezoneSelectInput = ({ source, label = 'Timezone', ...props }) => (
	<SelectInput
		source={source}
		label={label}
		choices={IANA_TIMEZONES}
		defaultValue={DEFAULT_TIMEZONE}
		style={TIMEZONE_INPUT_STYLE}
		{...props}
	/>
);

/**
 * The one timezone picker for report query parameters. Same choices and default
 * the report screens already used - this is a de-duplication, not a change: the
 * identical `choicesTimezone` array was declared separately in publishers.js,
 * carbon/discrepancies.js and carbon/pubreports.js.
 */
export const ReportTimezoneSelectInput = ({ source = 'timezone', label = 'Timezone', ...props }) => (
	<SelectInput
		source={source}
		label={label}
		choices={REPORT_TIMEZONES}
		defaultValue={DEFAULT_REPORT_TIMEZONE}
		{...props}
	/>
);

export const PACES = [
	{ id: 0, name: '0%' },
	{ id: 1, name: '1%' },
	{ id: 2, name: '2%' },
	{ id: 3, name: '3%' },
	{ id: 4, name: '4%' },
	{ id: 5, name: '5%' },
	{ id: 6, name: '6%' },
	{ id: 7, name: '7%' },
	{ id: 8, name: '8%' },
	{ id: 9, name: '9%' },
	{ id: 10, name: '10%' },
	{ id: 11, name: '11%' },
	{ id: 12, name: '12%' },
	{ id: 15, name: '15%' },
	{ id: 20, name: '20%' },
	{ id: 25, name: '25%' },
	{ id: 30, name: '30%' },
	{ id: 35, name: '35%' },
	{ id: 40, name: '40%' },
	{ id: 45, name: '45%' },
	{ id: 50, name: '50%' },
	{ id: 55, name: '55%' },
	{ id: 60, name: '60%' },
	{ id: 65, name: '65%' },
	{ id: 70, name: '70%' },
	{ id: 75, name: '75%' },
	{ id: 80, name: '80%' },
	{ id: 85, name: '85%' },
	{ id: 90, name: '90%' },
	{ id: 95, name: '95%' },
	{ id: 99, name: '99%' },
	{ id: 100, name: '100%' },
];

// NBA-23. How a video ad pod's reported bid price is computed.
// FIRST_PRICE is the safe default: both ads serve and both trackers fire, but the
// publisher is credited only the winner's price, so nothing is exposed while slot-2
// render rate is still unknown. Intended for the measurement window only.
export const POD_PRICING_MODES = [
	{ id: 'FIRST_PRICE', name: 'First price only (safe, measurement)' },
	{ id: 'COMPOUND', name: 'Compound P1 + P2 (full pod revenue)' }
];

// Leave unset to inherit the bidder's own second-price setting. An explicit
// FIRST_PRICE here overrides that fallback and always charges the full bid.
export const AUCTION_TYPES = [
	{ id: 'FIRST_PRICE', name: 'First price (charge the full bid)' },
	{ id: 'SECOND_PRICE', name: 'Second price (charge just above the runner-up)' }
];

/*
 * TWO helper texts, and they are not interchangeable.
 *
 * Different audiences, different voice: the ops copy describes the mechanism in
 * the third person, the advertiser copy addresses the buyer directly about what
 * a win costs them.
 *
 * The advertiser one MUST NOT mention bidders. Per NBU-5 no supply-side
 * identity reaches an advertiser role, and a sentence like "inherits the
 * bidder's setting" tells them a bidder exists and has a say in what they pay.
 * That is why these stay two separate constants rather than one string with a
 * conditional clause - that shape invites the next edit to improve the wording
 * and leak the supply side back in.
 *
 * Note the UI no longer documents the tri-state. Unset still means "inherit the
 * bidder's setting", which is NOT the same as FIRST_PRICE; see AUCTION_TYPES
 * above and NBA-38.
 */
export const AUCTION_TYPE_HELPER_TEXT_OPS =
	'Second price charges the winner just above the next eligible bid, or the floor if ' +
	'uncontested.';

export const AUCTION_TYPE_HELPER_TEXT_ADVERTISER =
	'First price charges your full bid whenever you win. Second price charges just above the ' +
	'next highest bid instead, so you often pay less than you bid for the same impression. ' +
	'Neither setting changes whether you win - only what a win costs.';

/**
 * Deliberately no initialValue or defaultValue. An unset auction_type is a
 * meaningful third state (inherit), and the rollout depends on every existing
 * campaign staying unset until someone opts it in - see NBA-38. Defaulting the
 * control here would write a value into every campaign on its next save.
 */
export const AuctionTypeSelectInput = ({
	source = 'auction_type',
	label = 'Auction Type',
	helperText = AUCTION_TYPE_HELPER_TEXT_OPS,
	...props
}) => (
	<SelectInput
		source={source}
		label={label}
		allowEmpty
		choices={AUCTION_TYPES}
		helperText={helperText}
		{...props}
	/>
);

export const SITE_TYPES = [
  { id: "site", name: 'Site' },
  { id: 'app', name: 'App' }
];

export function formatSiteName(site) {
	if (site && site.publisher_name) {
		return `${site.name} (${site.publisher_name})`
	} else if (site && site.adunit_path) {
		const pathArr = site.adunit_path.split('/')
		return `${pathArr[pathArr.length-1]} (${pathArr[pathArr.length-2]})`
	};
	return ''
}

export function siteFormFilter(searchText, formData) {
	if (formData?.publisher_id?.length > 0) return { search_name: searchText, publisher_id: formData.publisher_id };
	else return { search_name: searchText };
}

export function getESTTime () {
  const now = new Date(); // Represents current time (in local timezone)
  // Format for New York time
  const optionsNewYork = {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
  };
  return new Intl.DateTimeFormat('en-US', optionsNewYork).format(now);
}

export function getUsername () {
 return localStorage.getItem('usr');
}

// Returns the current tenant's display name (e.g. "NoBid", "Acme") for use in
// user-facing labels. Sourced from the org branding persisted at login
// (authProvider stores the login response branding in localStorage.nobid_branding,
// where display_name is set to the org's name). Falls back to the slug-fetched
// branding on window, then to 'NoBid' for the default tenant.
export function getOrgName () {
  try {
    const branding = JSON.parse(localStorage.getItem('nobid_branding'));
    if (branding && branding.display_name) return branding.display_name;
  } catch (e) { /* ignore malformed branding */ }
  return (window.nobid_branding && window.nobid_branding.display_name) || 'NoBid';
}

// Builds a bidder change-log line, e.g. "[6/23/2026, 3:04:05 PM (EST) by jdoe] - created".
// `action` is a verb such as 'created' or 'update'; `reason` is optional free text.
export function buildBidderLogMessage (action, reason) {
  let msg = `[${getESTTime()} (EST) by ${getUsername()}] - ${action}`;
  if (reason) msg += ` reason: ${reason}`;
  return msg;
}

// Prepends a change-log line to data.bidder_info.log, creating the container as needed.
// Shared by BidderCreate (transform) and BidderEdit (toolbar transformData).
export function prependBidderLog (data, action, reason) {
  const msg = buildBidderLogMessage(action, reason);
  if (!data.bidder_info) data.bidder_info = {};
  data.bidder_info.log = data.bidder_info.log ? `${msg}\n${data.bidder_info.log}` : msg;
  return data;
}

// --- Ads.txt search pattern helpers --------------------------------------
// The bidder ads.txt matcher (insights service/adstxtCrawler.js) compiles
// adstxt_config.regex with `new RegExp(regex, 'gim')` and tests it against an
// ads.txt body. Regex is unfriendly for ad-ops, so the UI lets users enter
// one entry per line as:
//   domain, account_id, relationship[, certification_authority_id]
// where relationship is direct | reseller | both (blank = both) and the optional
// 4th column is the certification authority ID (a free-form varchar, e.g.
// f5ab79cb980f11d1 or 987654321). A blank account ID ("nobid.io,") is the
// domain-only form: match any account on that seller domain. Entries are OR'd,
// so a site matches when any one of them is present -- there is deliberately no
// AND, because adstxt_domain_crawler_etl.js tests the regex line by line and no
// single ads.txt line can satisfy two entries at once. These helpers convert
// between that friendly form and the regex actually stored/consumed.
const ADSTXT_SEP = '[ ]*,[ ]*'; // matches a comma with optional surrounding spaces
const ADSTXT_BOTH = '(direct|reseller)';

function escapeRegexLiteral (s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Friendly text -> regex string. Returns '' when there are no usable lines.
export function adstxtPatternsToRegex (text) {
  if (!text || !text.trim()) return '';
  const parts = [];
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    const cols = line.split(',').map(c => c.trim());
    if (cols.length < 2 || !cols[0]) continue; // need at least "domain,"
    if (!cols[1]) {
      // Domain-only entry: match any account on this seller domain. The trailing
      // separator is load-bearing -- it forces a comma after the domain, so
      // `nobid.io,` cannot match `nobid.iox, 1, DIRECT`.
      parts.push(escapeRegexLiteral(cols[0]) + ADSTXT_SEP);
      continue;
    }
    const rel = (cols[2] || 'both').toLowerCase();
    const relRe = rel === 'direct' ? 'direct' : rel === 'reseller' ? 'reseller' : ADSTXT_BOTH;
    const segs = [escapeRegexLiteral(cols[0]), escapeRegexLiteral(cols[1]), relRe];
    if (cols[3]) segs.push(escapeRegexLiteral(cols[3])); // optional certification authority id (varchar)
    parts.push(segs.join(ADSTXT_SEP) + '[ ]*');
  }
  return parts.join('|');
}

// --- Bidder cookie-sync URL helpers --------------------------------------
// The platform composes a sync as: partnerEndpoint + urlencode(
//   `${NOBID_SYNC_HOST}/sync?pid=<bidder code>&uid=<partner macro>`)
// (see adserver-public sync.js nobidSync()). Hand-building that — especially the
// URL-encoded NoBid callback — is error-prone, so the UI collects just the
// partner endpoint + macro and the bidder code fills pid. NOTE: the authoritative
// sync_url is composed server-side (insights bidderCreate/bidderUpdate) because
// the code is auto-generated and not known to the form at create time. These
// helpers drive the read-only live preview and the legacy back-fill.
const NOBID_SYNC_HOST = 'https://ads.servenobid.com';
const NOBID_SYNC_CALLBACK_PREFIX = encodeURIComponent(`${NOBID_SYNC_HOST}/sync`); // https%3A%2F%2Fads.servenobid.com%2Fsync

// Composes the full sync URL. `code` may be undefined (create) — pid then shows
// a placeholder; the real value is filled server-side on save.
export function composeBidderSyncUrl (endpoint, macro, code) {
  if (!endpoint || !endpoint.trim()) return '';
  const pid = (code === undefined || code === null || code === '') ? '<assigned on save>' : code;
  const callback = `${NOBID_SYNC_HOST}/sync?pid=${pid}&uid=${(macro || '').trim()}`;
  return endpoint.trim() + encodeURIComponent(callback);
}

// Best-effort parse of an existing sync URL back into { endpoint, macro }.
// Returns null when it doesn't contain the NoBid callback (e.g. hand-built URL).
export function parseBidderSyncUrl (syncUrl) {
  if (!syncUrl || typeof syncUrl !== 'string') return null;
  const idx = syncUrl.toLowerCase().indexOf(NOBID_SYNC_CALLBACK_PREFIX.toLowerCase());
  if (idx < 0) return null;
  const endpoint = syncUrl.substring(0, idx);
  let callback;
  try { callback = decodeURIComponent(syncUrl.substring(idx)); } catch (e) { return null; }
  const m = callback.match(/[?&]uid=([^&]*)/i);
  return { endpoint, macro: m ? m[1] : '' };
}

// Regenerates data.adstxt_config.regex from the friendly patterns text on save.
// Only overwrites when patterns is non-empty, so a bidder whose regex was set by
// other means (and has no friendly patterns) is never blanked.
export function applyAdstxtRegex (data) {
  const cfg = data && data.adstxt_config;
  if (cfg && typeof cfg.patterns === 'string' && cfg.patterns.trim()) {
    cfg.regex = adstxtPatternsToRegex(cfg.patterns);
  }
  return data;
}

// rtb_connection_info.connection_headers is stored in Mongo as a Map<String,String>
// (that's what the adserver reads via @Field("connection_headers")). react-admin's
// ArrayInput can't bind directly to a map, so the bidder edit form uses a derived
// `connection_headers_list` array of {key, value} entries. These two helpers keep
// the two forms in sync — materializeConnectionHeadersList on load, and
// applyConnectionHeadersMap on save (which also deletes the temp list so we don't
// dual-write into Mongo).

// Materialize connection_headers_list from connection_headers for the form's
// ArrayInput. Called on record load. Idempotent: if _list already exists, leave
// it alone (that's the WIP state during a save round-trip).
export function materializeConnectionHeadersList (record) {
  if (!record) return record;
  const info = record.rtb_connection_info;
  if (!info || info.connection_headers_list) return record;
  const map = info.connection_headers;
  if (!map || typeof map !== 'object') return record;
  return {
    ...record,
    rtb_connection_info: {
      ...info,
      connection_headers_list: Object.entries(map).map(([key, value]) => ({ key, value }))
    }
  };
}

// Rebuild connection_headers (Map) from connection_headers_list on save, and
// drop the temp _list field so Mongo doesn't hold a stale copy. If the user
// cleared every row, we still write an empty map — that's meaningful (means
// "no headers"), and lets the operator remove all headers via the UI.
export function applyConnectionHeadersMap (data) {
  const info = data && data.rtb_connection_info;
  if (!info) return data;
  if (Array.isArray(info.connection_headers_list)) {
    info.connection_headers = Object.fromEntries(
      info.connection_headers_list
        .filter(kv => kv && typeof kv.key === 'string' && kv.key.trim())
        .map(kv => [kv.key.trim(), kv.value])
    );
    delete info.connection_headers_list;
  }
  return data;
}

// Regex string -> friendly text. Best-effort: only parses regexes produced by
// adstxtPatternsToRegex (our own format). Returns '' if it doesn't match that
// shape, so legacy/hand-written regexes are left untouched (not back-filled).
export function adstxtRegexToPatterns (regex) {
  if (!regex || !regex.trim()) return '';
  const BOTH_TOKEN = '';
  const normalized = regex.trim().replace(/\(direct\|reseller\)/gi, BOTH_TOKEN);
  const unescape = s => s.replace(/\\(.)/g, '$1');
  const stripTrail = s => s.replace(/\[ \]\*/g, '').trim();
  const lines = [];
  for (let entry of normalized.split('|')) {
    entry = entry.trim();
    if (!entry) continue;
    const cols = entry.split(ADSTXT_SEP);
    // Domain-only entry compiles to `domain` + separator, so it splits into the
    // domain plus a trailing empty column.
    if (cols.length === 2 && cols[1] === '') {
      lines.push(`${unescape(stripTrail(cols[0]))},`);
      continue;
    }
    if (cols.length < 3 || cols.length > 4) return ''; // not our format -> bail
    const domain = unescape(stripTrail(cols[0]));
    const account = unescape(stripTrail(cols[1]));
    let rel = stripTrail(cols[2]);
    rel = rel === BOTH_TOKEN ? 'both' : rel.toLowerCase();
    if (!['direct', 'reseller', 'both'].includes(rel)) return '';
    if (cols.length === 4) {
      lines.push(`${domain}, ${account}, ${rel}, ${unescape(stripTrail(cols[3]))}`);
    } else {
      lines.push(`${domain}, ${account}, ${rel}`);
    }
  }
  return lines.join('\n');
}

export function formDataFieldsQueryStringFormat(formData, fields) {
	const buf = [];
	for(let i in fields) {
		let field = fields[i];
		if (formData[field] && Array.isArray(formData[field]) && formData[field].length>0) {
			formData[field].map(x => buf.push(`${field}=${x}`));
		} else if (formData[field]) {
			buf.push(`${field}=${formData[field]}`);
		}
	}
	return buf.length>0 ? `&${buf.join('&')}` : '';
}

export function MetricBox({ label, tooltip, color, children }) {
  const hasColor = !!color;
  const content = (
    <Paper variant={hasColor ? 'elevation' : 'outlined'} elevation={hasColor ? 0 : undefined}
      style={{ padding: '8px 12px 4px', textAlign: 'center', minWidth: '70px', borderRadius: '6px',
        backgroundColor: hasColor ? color : undefined,
        border: hasColor ? `1px solid rgba(0,0,0,0.15)` : undefined,
        boxShadow: hasColor ? '0 1px 3px rgba(0,0,0,0.15)' : undefined,
        cursor: tooltip ? 'default' : undefined }}>
      {children}
      <Typography variant="caption" style={{ fontSize: '10px', lineHeight: 1, color: hasColor ? 'rgba(255,255,255,0.85)' : undefined }}>{label}</Typography>
    </Paper>
  );
  if (tooltip) {
    return <Tooltip title={tooltip}>{content}</Tooltip>;
  }
  return content;
}

export function progressBar (nom, denom) {
  if (!(denom > 0)) return '';

  // A campaign the budget coordinator has never processed carries NO current_*
  // counters, so `nom` arrives undefined. Left unhandled that produced
  // `width: NaN%` - invalid CSS, which the browser DROPS, letting the fill
  // default to the full track - plus orangered (because `undefined <= denom`
  // is false) and the label "%NaN". "No data yet" therefore rendered as a
  // full over-limit bar. Show an empty track and a dash instead.
  const value = Number(nom);
  const hasData = Number.isFinite(value);
  const pct = hasData ? Math.min(100, (100 * value) / denom) : 0;
  const color = !hasData || value <= denom ? '#00ff7f' : 'orangered';

  return (
    <div style={{ position: 'relative', width: '80px', height: '16px', overflow: 'hidden', background: '#eee' }}>
      <div style={{ width: pct + '%', height: '100%', background: color }} />
      <span style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 'bold', color: '#333' }}>
        {hasData ? double((100 * value) / denom, '%') : '-'}
      </span>
    </div>
  );
}

/**
 * The single "this limit is exhausted" badge. Used beside the campaign spend
 * limit inputs, in the advertiser delivery tables, and on the delivery cards -
 * one look everywhere, so the same campaign cannot be styled two ways on one
 * screen (NBU-4).
 */
export const PausedChip = () => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '1px 8px',
    borderRadius: '10px',
    border: '1px solid orangered',
    color: 'orangered',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    whiteSpace: 'nowrap'
  }}>
    <WarningIcon style={{ fontSize: '12px' }} /> PAUSED
  </span>
);

/**
 * Renders the PAUSED badge for an exhausted `*_limit_active` flag.
 *
 * ONLY an explicit `false` is paused. The budget coordinator ETL writes these
 * flags per cycle, so an absent flag means "not written yet" - unknown, not
 * exhausted. This function used to test falsiness, which reported every
 * never-processed campaign as PAUSED. Keep the `=== false` test: the delivery
 * cards apply the same rule, and the two render side by side.
 */
export function pausedIndicator (isActive) {
  return isActive === false ? <PausedChip /> : '';
}

/**
 * Two-column form grid, the layout SectionHeader is built for (it spans both
 * columns via gridColumn '1 / -1').
 *
 * Inputs inside a grid Box are NOT wrapped in react-admin's FormInput, so they
 * get no automatic width - give each one an explicit width or maxWidth, the way
 * bidders.js does.
 *
 * NOTE: this same object is still declared locally in bidders.js, campaigns.js,
 * audiences.js, deals.js and adcampaigns.js. Those copies are left alone rather
 * than swept up in an unrelated change; point them here when one of them is
 * next touched.
 */
export const twoColumnGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', width: '50%' };

// Grid item spanning the full width of twoColumnGrid - for a wide input or a
// standalone note that should not sit in a column.
export const gridFullWidth = { gridColumn: '1 / -1' };

export const SectionHeader = ({ title }) => (
  <Box style={{ gridColumn: '1 / -1', marginTop: '16px', marginBottom: '8px' }}>
    <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#90caf9' }}>{title}</Typography>
    <Divider style={{ marginTop: '4px' }} />
  </Box>
);

/**
 * SimpleFormIterator variant that lays each row's inputs out side by side.
 *
 * The stock iterator stacks every child of a row on its own line, which burns a
 * lot of vertical space on narrow pairs like a bidder's connection headers
 * (Key / Value). This override only restyles the <section> the iterator already
 * wraps a row's inputs in - the reorder arrows, index and Remove button keep
 * their normal positions - and wraps onto a second line when the viewport is
 * too narrow to fit the inputs.
 *
 * Only `form` is overridden; every other class keeps the react-admin default.
 */
const useInlineFormIteratorStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    '& > .ra-input': {
      marginRight: theme.spacing(2)
    }
  }
}));

export const InlineFormIterator = (props) => {
  const classes = useInlineFormIteratorStyles();
  return <SimpleFormIterator classes={classes} {...props} />;
};

// Shared helper for custom (non-CRUD) console API calls.
// Same auth convention as dataprovider.js: bearer token from localStorage.
export async function apiRequest (path, { method = 'GET', body } = {}) {
  const url = `${window.nobid_endpoint}${path}`;
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
    throw new Error(err.message || err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

// --- Creative preview ----------------------------------------------------
// Shared by the ops creative form (src/creatives.js) and the advertiser
// self-serve creative form (src/advertiser/creatives.js). Lives here rather
// than in either page file because both need it and neither owns it.
//
// Read-only: renders whatever is currently in form state. It issues no
// requests and reads no record beyond the formData handed to it, which is why
// it is safe for the advertiser role even though the surrounding form is
// field-restricted.
//
// Handles every ctype the platform has, not just the four an advertiser can
// create (url, html, video, native - see CTYPES in insights
// routes/api/v1/validators.js). `image` and `vast` are ops-only and reachable
// solely from the ops form.

export const HtmlIframePreview = ({ markup, width, height }) => {
  const iframeRef = useRef(null);
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><style>body{margin:0;overflow:hidden;}</style></head><body>${markup}</body></html>`);
    doc.close();
  }, [markup]);
  return (
    <iframe
      ref={iframeRef}
      title="HTML Creative Preview"
      width={width}
      height={height}
      style={{ border: '1px dashed #ccc' }}
    />
  );
};

export const CreativePreviewContent = ({ formData }) => {
  if (!formData) return <Typography>No creative data available.</Typography>;

  const { ctype, width, height, name } = formData;
  const previewWidth = Math.min(width || 300, 970);
  const previewHeight = Math.min(height || 250, 1050);
  // Persisted records store image as an array (image[0].src, see insights
  // common.js processCreativeCreateOrUpdate); a freshly dropped file is a
  // single object. Normalize so the preview works in both states.
  const image = Array.isArray(formData.image) ? formData.image[0] : formData.image;

  if (ctype === 'image' && image?.src) {
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>Image Creative Preview</Typography>
        <Box style={{ border: '1px dashed #ccc', padding: 8, display: 'inline-block' }}>
          <img
            src={image.src}
            alt={formData.title || name || 'Creative'}
            style={{ maxWidth: previewWidth, maxHeight: previewHeight, display: 'block' }}
          />
        </Box>
        {formData.click_url && (
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Click URL: <Link href={formData.click_url} target="_blank" rel="noopener">{formData.click_url}</Link>
          </Typography>
        )}
      </Box>
    );
  }

  if (ctype === 'url' && formData.image_url) {
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>Image URL Creative Preview</Typography>
        <Box style={{ border: '1px dashed #ccc', padding: 8, display: 'inline-block' }}>
          <img
            src={formData.image_url}
            alt={formData.title || name || 'Creative'}
            style={{ maxWidth: previewWidth, maxHeight: previewHeight, display: 'block' }}
          />
        </Box>
        {formData.click_url && (
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Click URL: <Link href={formData.click_url} target="_blank" rel="noopener">{formData.click_url}</Link>
          </Typography>
        )}
      </Box>
    );
  }

  if (ctype === 'html' && formData.markup) {
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>HTML Creative Preview</Typography>
        <HtmlIframePreview markup={formData.markup} width={previewWidth} height={previewHeight} />
      </Box>
    );
  }

  if (ctype === 'video' && formData.video_url) {
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>Video Creative Preview</Typography>
        <video
          src={formData.video_url}
          controls
          width={Math.min(previewWidth, 640)}
          style={{ border: '1px dashed #ccc', display: 'block' }}
        >
          Your browser does not support video playback.
        </video>
        {formData.video_click_url && (
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Click URL: <Link href={formData.video_click_url} target="_blank" rel="noopener">{formData.video_click_url}</Link>
          </Typography>
        )}
      </Box>
    );
  }

  if (ctype === 'vast' && formData.markup) {
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>VAST Creative Preview</Typography>
        <Typography variant="body2" gutterBottom>
          Test your VAST tag{' '}
          <Link href="https://googleads.github.io/googleads-ima-html5/vsi/" target="_blank" rel="noopener">here</Link>
        </Typography>
        <Box style={{ border: '1px dashed #ccc', padding: 12, maxWidth: 800, maxHeight: 400, overflow: 'auto', backgroundColor: '#f5f5f5' }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>{formData.markup}</pre>
        </Box>
      </Box>
    );
  }

  if (ctype === 'native') {
    // A native creative's image can arrive either way: the ops form uploads it
    // (image[0].src), while image_url is the only path the advertiser form has
    // and the only one /api/v1 accepts. Reading image?.src alone rendered every
    // url-sourced native with no image at all - silently, since the rest of the
    // card still populated.
    const nativeImageSrc = image?.src || formData.image_url;
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>Native Creative Preview</Typography>
        <Card style={{ maxWidth: 400 }}>
          {nativeImageSrc && (
            <CardMedia
              component="img"
              image={nativeImageSrc}
              alt={formData.title || name || 'Native Creative'}
              style={{ maxHeight: 250 }}
            />
          )}
          <CardContent>
            {formData.sponsor && (
              <Typography variant="caption" color="textSecondary">Sponsored by {formData.sponsor}</Typography>
            )}
            {formData.title && (
              <Typography variant="h6" gutterBottom>{formData.title}</Typography>
            )}
            {formData.description && (
              <Typography variant="body2" color="textSecondary">{formData.description}</Typography>
            )}
          </CardContent>
          {(formData.call_to_action_button_text || formData.click_url) && (
            <CardActions>
              {formData.call_to_action_button_text && (
                <Button size="small" color="primary" variant="contained"
                  href={formData.click_url || '#'} target="_blank" rel="noopener">
                  {formData.call_to_action_button_text}
                </Button>
              )}
            </CardActions>
          )}
        </Card>
      </Box>
    );
  }

  return (
    <Typography color="textSecondary">
      No preview available. Please fill in the creative settings first.
    </Typography>
  );
};

// Shared color input: native color swatch bound to the same source as a text
// field, so named colors (e.g. "silver") and hex (e.g. "#edf1ef") both work.
export const ColorInput = (props) => {
  const { input } = useInput(props);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <input
        type="color"
        value={input.value || '#000000'}
        onChange={(e) => input.onChange(e.target.value)}
        style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', background: 'none' }}
      />
      <TextInput {...props} style={{ width: 160, ...(props.style || {}) }} helperText={props.helperText} />
    </div>
  );
};
